import { useQuery } from '@apollo/client'
import chunk from 'lodash.chunk'
import React, { createContext, useEffect, useMemo, useState } from 'react'
import { FETCH_ALL } from '../graphql/queries'
import { GlobalType } from '../types'
import { useWorkspace } from './workspace'

export type GlobalState = {
  other?: string
} & GlobalType

export const GlobalStateContext = createContext<GlobalState>({} as GlobalType)

export const GlobalStateProvider: React.FC = ({ children }) => {
  const [globalState, setGlobalState] = useState<GlobalState>({} as GlobalType)
  const { selectedWorkspace } = useWorkspace(
    ({ selectedWorkspace }) => ({
      selectedWorkspace
    }),
    (state, newState) => {
      return state?.selectedWorkspace?._id === newState?.selectedWorkspace?._id
    }
  )
  const { data, loading, refetch } = useQuery<GlobalType>(FETCH_ALL, {
    skip: !selectedWorkspace
  })

  const cacheImages = async (srcArray: string[]) => {
    const promises = await srcArray.map(src => {
      return new Promise<void>(function (resolve, reject) {
        const img = new Image()

        img.src = src
        img.onload = () => resolve()
        img.onerror = () => reject(new Error('Erro ao carregar imagem'))
      })
    })
    await Promise.all(promises)
  }

  const cacheIframes = async (srcArray: string[]) => {
    const loadIframe = (id: string, src: string) => {
      return new Promise(resolve => {
        const frame = document.createElement('iframe')
        frame.src = src
        frame.id = id
        frame.style.width = '1px'
        frame.style.height = '1px'
        frame.style.position = 'absolute'
        frame.style.top = '1px'
        frame.onload = resolve
        frame.onerror = resolve
        frame.onabort = resolve
        document.body.appendChild(frame)
      })
    }

    const srcChunks: Array<Array<string>> = chunk(srcArray, 10)
    const chunksPromises = srcChunks.map(async chunk => {
      const promises = chunk.map(async src => {
        const id = Math.floor(Math.random() * (12 - 0 + 1) + 0)
        await loadIframe(`${id}-iframe`, src)
        document.getElementById(`${id}-iframe`)?.remove()
      })
      return await Promise.all(promises)
    })
    await Promise.all(chunksPromises)
  }

  const handleCache = async () => {
    if (data) {
      const getImages = () => {
        const imgs: string[] = []
        data.iolTypes.forEach(iol => {
          if (iol) {
            if (iol.simulation) {
              imgs.push(iol.simulation.after)
              imgs.push(iol.simulation.before)
            }
            imgs.push(iol.thumb)
          }
        })
        data.iolLibraries.forEach(iol => {
          if (iol) {
            imgs.push(iol.thumb)
          }
        })
        return imgs
      }

      // CACHE IFRAMES
      cacheIframes(
        data.iolLibraries.map(iol => iol?.iframe || '').filter(Boolean)
      )
      const imgs = getImages()
      cacheImages(imgs)

      setGlobalState(data)
    }
  }

  useEffect(() => {
    handleCache()
  }, [data])

  const isLoading = useMemo(() => {
    if (!selectedWorkspace) return false
    return loading || Object.keys(globalState).length === 0
  }, [globalState, loading, selectedWorkspace])

  return (
    <GlobalStateContext.Provider
      value={{
        ...globalState,
        loading: isLoading,
        refetch
      }}
    >
      {children}
    </GlobalStateContext.Provider>
  )
}
export const useGlobalState = (): GlobalType =>
  React.useContext(GlobalStateContext)
